import { Link } from 'react-router-dom';
import { Infographie } from '@/hooks/useInfographies';

interface InfographiesGridProps {
  infographies: Infographie[];
  groupByCategory?: boolean;
}

export function InfographiesGrid({ infographies, groupByCategory = false }: InfographiesGridProps) {
  if (!groupByCategory) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {infographies.map((infographie) => (
          <InfographieCard key={infographie.id} infographie={infographie} />
        ))}
      </div>
    );
  }

  // Grouper par catégorie
  const groupedInfographies = infographies.reduce((acc, infographie) => {
    const category = infographie.category || 'Autre';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(infographie);
    return acc;
  }, {} as Record<string, Infographie[]>);

  return (
    <div className="space-y-16">
      {Object.entries(groupedInfographies).map(([category, items]) => (
        <div key={category}>
          <h3 className="text-2xl font-heading font-bold text-aspexit-black mb-8">
            {category}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {items.map((infographie) => (
              <InfographieCard key={infographie.id} infographie={infographie} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function InfographieCard({ infographie }: { infographie: Infographie }) {
  return (
    <Link
      to={`/infographies/${infographie.slug}`}
      className="group flex flex-col h-full bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
    >
      {/* Image */}
      <div className="aspect-[16/9] overflow-hidden bg-gray-100 flex-shrink-0">
        <img
          src={infographie.image_url}
          alt={infographie.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        />
      </div>

      {/* Contenu */}
      <div className="p-6 flex flex-col flex-grow">
        {/* Category Tag */}
        {infographie.category && (
          <span className="inline-block px-3 py-1 bg-[#FF6B6B]/10 text-[#FF6B6B] text-xs font-semibold rounded-full mb-3 self-start">
            {infographie.category}
          </span>
        )}

        <h3 className="text-xl font-heading font-semibold text-aspexit-black mb-3 group-hover:text-[#FF6B6B] transition-colors line-clamp-2 min-h-[3.5rem]">
          {infographie.title}
        </h3>
        <p className="text-aspexit-gray text-sm line-clamp-3 min-h-[4rem]">
          {infographie.description}
        </p>
      </div>
    </Link>
  );
}
